/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Table} from '@dropbox/dig-components/table';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {
  AdminWorkstreamImporterCreatedWorkstream,
  AdminWorkstreamImporterCreateResponse,
  AdminWorkstreamImporterMetricPreview,
  AdminWorkstreamImporterMilestonePreview,
  AdminWorkstreamImporterPreviewResponse,
  AdminWorkstreamImporterRowPreview,
  AdminWorkstreamImporterService,
  ApiError,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {CSSProperties, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';

import {AdminWorkstreamImporterCreatedStatus} from './AdminWorkstreamImporterCreatedStatus';
import {AdminWorkstreamImporterTimeframeSelect} from './AdminWorkstreamImporterTimeframeSelect';

const LIST_STYLE: CSSProperties = {listStyleType: 'disc', margin: 0, paddingLeft: 18};
const NESTED_LIST_STYLE: CSSProperties = {
  listStyleType: 'disc',
  margin: '4px 0 0',
  paddingLeft: 16,
};
const LIST_ITEM_STYLE: CSSProperties = {
  display: 'list-item',
  listStyleType: 'disc',
  marginBottom: 8,
};
const NESTED_LIST_ITEM_STYLE: CSSProperties = {display: 'list-item', listStyleType: 'disc'};
const REVIEW_TABLE_STYLE: CSSProperties = {minWidth: 1600};
const REVIEW_CELL_STYLE: CSSProperties = {verticalAlign: 'top'};
const WIDE_REVIEW_COLUMN_STYLE: CSSProperties = {minWidth: 300};
const WIDE_REVIEW_HEADER_CELL_STYLE: CSSProperties = {
  ...REVIEW_CELL_STYLE,
  ...WIDE_REVIEW_COLUMN_STYLE,
};
const MULTILINE_CELL_STYLE: CSSProperties = {whiteSpace: 'pre-line'};
const ERROR_HIGHLIGHT_STYLE: CSSProperties = {
  backgroundColor: 'var(--dig-color__background__critical-subtle, #fff4f2)',
  boxShadow: 'inset 3px 0 0 var(--dig-color__border__critical, #d92d20)',
};
const WARNING_HIGHLIGHT_STYLE: CSSProperties = {
  backgroundColor: 'var(--dig-color__warning__surface--state-1, #fff8e5)',
  boxShadow: 'inset 3px 0 0 var(--dig-color__warning__base, #996800)',
};
const ERROR_ITEM_STYLE: CSSProperties = {
  ...ERROR_HIGHLIGHT_STYLE,
  borderRadius: 4,
  padding: '4px 6px',
};
const STATUS_EMOJI_STYLE: CSSProperties = {
  display: 'inline-block',
  fontSize: 18,
  lineHeight: '24px',
};
const METRIC_BADGES_STYLE: CSSProperties = {
  display: 'inline-flex',
  gap: 4,
  marginLeft: 4,
  verticalAlign: 'text-bottom',
};
const METRIC_BADGE_STYLE: CSSProperties = {
  display: 'inline-block',
  lineHeight: 1,
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    const detail = error.body?.detail;
    if (typeof detail === 'string') return detail;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
};

const personText = (
  person?: {name: string; ldap?: string | null} | null,
  fallback?: string | null
) => {
  if (person?.name) {
    return person.ldap ? `${person.name} (@${person.ldap})` : person.name;
  }
  return fallback || '-';
};

const optionalPersonText = (
  person?: {name: string; ldap?: string | null} | null,
  fallback?: string | null
) => {
  if (!person && !fallback) return null;
  return personText(person, fallback);
};

const PersonLabel = ({
  person,
  fallback,
}: {
  person?: {name: string; ldap?: string | null} | null;
  fallback?: string | null;
}) => {
  const label = personText(person, fallback);
  if (!person?.ldap) return <>{label}</>;

  return (
    <Link to={ROUTE_PATHS.PROFILE.replace(':userid', person.ldap)} openInNewTab monochromatic>
      {label}
    </Link>
  );
};

const presentStrings = (items: Array<string | null | undefined | false>) =>
  items.filter((item): item is string => Boolean(item));

type RowIssue = {
  kind: 'Error' | 'Warning';
  message: string;
};

const rowIssues = (row: AdminWorkstreamImporterRowPreview): RowIssue[] => [
  ...(row.errors ?? []).map((message) => ({kind: 'Error' as const, message})),
  ...(row.warnings ?? []).map((message) => ({kind: 'Warning' as const, message})),
];

const rowIssueText = (row: AdminWorkstreamImporterRowPreview) => {
  const issues = rowIssues(row);
  if (!issues.length) return 'No issues';

  return issues.map((issue) => `${issue.kind}: ${issue.message}`).join('\n');
};

const IssueTooltipContent = ({issues}: {issues: RowIssue[]}) => {
  if (!issues.length) return <Text size="small">No issues</Text>;

  return (
    <Box display="flex" flexDirection="column" style={{gap: 4}}>
      {issues.map((issue, index) => (
        <div
          key={`${issue.kind}-${issue.message}-${index}`}
          data-testid="workstream-importer-tooltip-issue"
        >
          <Text size="small">
            <strong>{issue.kind}:</strong> {issue.message}
          </Text>
        </div>
      ))}
    </Box>
  );
};

const workstreamCellWarnings = (row: AdminWorkstreamImporterRowPreview): string[] => {
  if (!row.workstream_exists) return [];
  const warnings = (row.warnings ?? []).filter((warning) =>
    warning.startsWith('Workstream already exists in CompanyOS')
  );
  if (warnings.length) return warnings;
  return [`Workstream already exists in CompanyOS: ${row.workstream_name}`];
};

const workstreamCellErrors = (row: AdminWorkstreamImporterRowPreview): string[] =>
  (row.errors ?? []).filter(
    (error) =>
      error === 'Missing required value: Workstream' ||
      error.startsWith('Duplicate workstream name within import')
  );

const trimIssueSubject = (message: string, subject: string) => {
  const suffix = `: ${subject}`;
  return subject && message.endsWith(suffix) ? message.slice(0, -suffix.length) : message;
};

const trimPreviewItemSubject = (message: string, label: string, title: string) => {
  const quotedSubject = `${label} '${title}'`;
  const colonPrefix = `${quotedSubject}: `;
  if (message.startsWith(colonPrefix)) return message.slice(colonPrefix.length);

  if (message.startsWith(`${quotedSubject} is shared in import; `)) {
    return `Shared in import; ${message.slice(`${quotedSubject} is shared in import; `.length)}`;
  }

  if (message.startsWith(`${quotedSubject} has an invalid date `)) {
    return `Invalid date ${message.slice(`${quotedSubject} has an invalid date `.length)}`;
  }

  if (message.startsWith(`${quotedSubject} matches multiple existing active metrics`)) {
    return 'Matches multiple existing active metrics';
  }

  return message;
};

const previewItemIssues = (errors: string[], label: string, title: string): RowIssue[] =>
  errors.map((error) => ({
    kind: 'Error' as const,
    message: trimPreviewItemSubject(error, label, title),
  }));

const hasErrorField = (row: AdminWorkstreamImporterRowPreview, field: string) =>
  Boolean((row.error_fields ?? []).includes(field));

const hasPreviewItemErrors = (items?: Array<{errors?: string[]}>) =>
  Boolean(items?.some((item) => (item.errors ?? []).length));

const shouldHighlightReviewCell = (row: AdminWorkstreamImporterRowPreview, field: string) => {
  if (!hasErrorField(row, field)) return false;
  if (field === 'metrics' && hasPreviewItemErrors(row.metrics)) return false;
  if (field === 'milestones' && hasPreviewItemErrors(row.milestones)) return false;
  return true;
};

const reviewCellStyle = (
  row: AdminWorkstreamImporterRowPreview,
  field: string,
  baseStyle?: CSSProperties,
  hasWarning = false
) => {
  if (shouldHighlightReviewCell(row, field)) {
    return {...REVIEW_CELL_STYLE, ...baseStyle, ...ERROR_HIGHLIGHT_STYLE};
  }
  if (hasWarning) {
    return {...REVIEW_CELL_STYLE, ...baseStyle, ...WARNING_HIGHLIGHT_STYLE};
  }
  return {...REVIEW_CELL_STYLE, ...baseStyle};
};

const metricBadges = (metric: AdminWorkstreamImporterMetricPreview) =>
  [
    metric.is_primary
      ? {icon: '⭐', label: 'North Star metric', testId: 'workstream-importer-metric-badge-primary'}
      : null,
    metric.shared_in_import
      ? {
          icon: '🔗',
          label: 'Linked to another workstream in this import',
          testId: 'workstream-importer-metric-badge-linked',
        }
      : null,
    metric.reuses_existing_metric
      ? {
          icon: '♻️',
          label: 'Reuses an existing CompanyOS metric',
          testId: 'workstream-importer-metric-badge-existing',
        }
      : null,
  ].filter((badge): badge is {icon: string; label: string; testId: string} => Boolean(badge));

const MetricBadges = ({badges}: {badges: ReturnType<typeof metricBadges>}) => {
  if (!badges.length) return null;

  return (
    <span style={METRIC_BADGES_STYLE}>
      {badges.map((badge) => (
        <Tooltip key={badge.label} title={badge.label} placement="top">
          <span
            aria-label={badge.label}
            data-testid={badge.testId}
            role="img"
            style={METRIC_BADGE_STYLE}
          >
            {badge.icon}
          </span>
        </Tooltip>
      ))}
    </span>
  );
};

const forecastLine = (
  forecast: NonNullable<AdminWorkstreamImporterMetricPreview['forecasts']>[number]
) => {
  const values = [
    forecast.budget ? `Budget: ${forecast.budget}` : '',
    forecast.target ? `Target: ${forecast.target}` : '',
  ].filter(Boolean);
  return values.length ? `${forecast.period}: ${values.join(', ')}` : forecast.period;
};

const MetricPreviewList = ({row}: {row: AdminWorkstreamImporterRowPreview}) => {
  const metrics = row.metrics ?? [];
  if (!metrics.length) return <Text size="small">-</Text>;

  return (
    <ul style={LIST_STYLE}>
      {metrics.map((metric, index) => {
        const badges = metricBadges(metric);
        const errors = metric.errors ?? [];
        const issues = previewItemIssues(errors, 'Metric', metric.title);
        const owner = optionalPersonText(metric.owner);
        const forecasts = metric.forecasts ?? [];
        const content = (
          <div>
            <Text size="small">
              {metric.title}
              <MetricBadges badges={badges} />
            </Text>
            {owner || forecasts.length ? (
              <ul style={NESTED_LIST_STYLE}>
                {owner ? (
                  <li style={NESTED_LIST_ITEM_STYLE}>
                    <Text size="small">
                      DRI: <PersonLabel person={metric.owner} />
                    </Text>
                  </li>
                ) : null}
                {forecasts.map((forecast) => (
                  <li
                    key={`${forecast.period}-${forecast.budget ?? ''}-${forecast.target ?? ''}`}
                    style={NESTED_LIST_ITEM_STYLE}
                  >
                    <Text size="small">{forecastLine(forecast)}</Text>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        );

        return (
          <li
            key={`${metric.title}-${index}`}
            aria-invalid={errors.length ? true : undefined}
            style={errors.length ? {...LIST_ITEM_STYLE, ...ERROR_ITEM_STYLE} : LIST_ITEM_STYLE}
          >
            {issues.length ? (
              <Tooltip title={<IssueTooltipContent issues={issues} />} placement="right">
                {content}
              </Tooltip>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ul>
  );
};

const MilestonePreviewList = ({row}: {row: AdminWorkstreamImporterRowPreview}) => {
  const milestones = row.milestones ?? [];
  if (!milestones.length) return <Text size="small">-</Text>;

  return (
    <ul style={LIST_STYLE}>
      {milestones.map((milestone: AdminWorkstreamImporterMilestonePreview, index) => {
        const errors = milestone.errors ?? [];
        const issues = previewItemIssues(errors, 'Milestone', milestone.title);
        const owner = optionalPersonText(milestone.owner);
        const eta = milestone.eta_text || milestone.eta;
        const detailLines = presentStrings([eta ? `ETA: ${eta}` : '']);
        const content = (
          <div>
            <Text size="small">{milestone.title}</Text>
            {owner || detailLines.length ? (
              <ul style={NESTED_LIST_STYLE}>
                {owner ? (
                  <li style={NESTED_LIST_ITEM_STYLE}>
                    <Text size="small">
                      DRI: <PersonLabel person={milestone.owner} />
                    </Text>
                  </li>
                ) : null}
                {detailLines.map((line) => (
                  <li key={line} style={NESTED_LIST_ITEM_STYLE}>
                    <Text size="small">{line}</Text>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        );

        return (
          <li
            key={`${milestone.title}-${index}`}
            aria-invalid={errors.length ? true : undefined}
            style={errors.length ? {...LIST_ITEM_STYLE, ...ERROR_ITEM_STYLE} : LIST_ITEM_STYLE}
          >
            {issues.length ? (
              <Tooltip title={<IssueTooltipContent issues={issues} />} placement="right">
                {content}
              </Tooltip>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ul>
  );
};

const StatusCell = ({
  row,
  createdWorkstream,
}: {
  row: AdminWorkstreamImporterRowPreview;
  createdWorkstream?: AdminWorkstreamImporterCreatedWorkstream;
}) => {
  if (createdWorkstream) {
    return <AdminWorkstreamImporterCreatedStatus workstream={createdWorkstream} />;
  }

  const hasWarnings = Boolean((row.warnings ?? []).length);
  const label = row.valid ? (hasWarnings ? 'Valid row with warnings' : 'Valid row') : 'Invalid row';
  const issues = rowIssues(row);
  const issueText = rowIssueText(row);
  const statusIcon = row.valid ? (hasWarnings ? '⚠️' : '✅') : '❌';

  return (
    <Tooltip title={<IssueTooltipContent issues={issues} />} placement="right">
      <span aria-description={issueText} aria-label={label} role="img" style={STATUS_EMOJI_STYLE}>
        {statusIcon}
      </span>
    </Tooltip>
  );
};

const ReviewTable = ({
  preview,
  result,
}: {
  preview: AdminWorkstreamImporterPreviewResponse;
  result: AdminWorkstreamImporterCreateResponse | null;
}) => {
  const createdByRowIndex = new Map(
    (result?.workstreams ?? []).map((workstream) => [workstream.row_index, workstream])
  );

  return (
    <Box style={{overflowX: 'auto', width: '100%'}}>
      <Table style={REVIEW_TABLE_STYLE}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={REVIEW_CELL_STYLE}>Status</Table.HeaderCell>
            <Table.HeaderCell style={REVIEW_CELL_STYLE}>Workstream</Table.HeaderCell>
            <Table.HeaderCell style={WIDE_REVIEW_HEADER_CELL_STYLE}>Objective</Table.HeaderCell>
            <Table.HeaderCell style={WIDE_REVIEW_HEADER_CELL_STYLE}>Metrics</Table.HeaderCell>
            <Table.HeaderCell style={WIDE_REVIEW_HEADER_CELL_STYLE}>Milestones</Table.HeaderCell>
            <Table.HeaderCell style={REVIEW_CELL_STYLE}>DRI</Table.HeaderCell>
            <Table.HeaderCell style={REVIEW_CELL_STYLE}>Headcount</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {preview.rows.map((row) =>
            (() => {
              const workstreamName = row.workstream_name ?? '';
              const workstreamIssues: RowIssue[] = [
                ...workstreamCellErrors(row).map((message) => ({
                  kind: 'Error' as const,
                  message: trimIssueSubject(message, workstreamName),
                })),
                ...workstreamCellWarnings(row).map((message) => ({
                  kind: 'Warning' as const,
                  message: trimIssueSubject(message, workstreamName),
                })),
              ];
              const workstreamIssueText = workstreamIssues
                .map((issue) => `${issue.kind}: ${issue.message}`)
                .join('\n');

              return (
                <Table.Row
                  key={row.row_index}
                  style={
                    row.row_index === 1
                      ? {backgroundColor: 'var(--dig-color__background__subtle)'}
                      : undefined
                  }
                >
                  <Table.Cell style={REVIEW_CELL_STYLE}>
                    <StatusCell
                      row={row}
                      createdWorkstream={createdByRowIndex.get(row.row_index)}
                    />
                  </Table.Cell>
                  <Table.Cell
                    aria-invalid={hasErrorField(row, 'workstream_name') ? true : undefined}
                    style={reviewCellStyle(
                      row,
                      'workstream_name',
                      undefined,
                      Boolean(workstreamIssues.some((issue) => issue.kind === 'Warning'))
                    )}
                  >
                    {workstreamIssues.length ? (
                      <Tooltip
                        title={<IssueTooltipContent issues={workstreamIssues} />}
                        placement="right"
                      >
                        <span
                          aria-description={workstreamIssueText}
                          data-testid="workstream-importer-workstream-issue"
                          style={{display: 'inline-block'}}
                        >
                          <Text>{row.workstream_name || '-'}</Text>
                        </span>
                      </Tooltip>
                    ) : (
                      <Text>{row.workstream_name || '-'}</Text>
                    )}
                  </Table.Cell>
                  <Table.Cell
                    aria-invalid={hasErrorField(row, 'objective') ? true : undefined}
                    style={reviewCellStyle(row, 'objective', WIDE_REVIEW_COLUMN_STYLE)}
                  >
                    <Text size="small">{row.objective || '-'}</Text>
                  </Table.Cell>
                  <Table.Cell
                    aria-invalid={hasErrorField(row, 'metrics') ? true : undefined}
                    style={reviewCellStyle(row, 'metrics', WIDE_REVIEW_COLUMN_STYLE)}
                  >
                    <MetricPreviewList row={row} />
                  </Table.Cell>
                  <Table.Cell
                    aria-invalid={hasErrorField(row, 'milestones') ? true : undefined}
                    style={reviewCellStyle(row, 'milestones', WIDE_REVIEW_COLUMN_STYLE)}
                  >
                    <MilestonePreviewList row={row} />
                  </Table.Cell>
                  <Table.Cell
                    aria-invalid={hasErrorField(row, 'dri') ? true : undefined}
                    style={reviewCellStyle(row, 'dri')}
                  >
                    <PersonLabel person={row.dri} fallback={row.dri_text} />
                  </Table.Cell>
                  <Table.Cell
                    aria-invalid={hasErrorField(row, 'headcount') ? true : undefined}
                    style={reviewCellStyle(row, 'headcount', MULTILINE_CELL_STYLE)}
                  >
                    {row.headcount || '-'}
                  </Table.Cell>
                </Table.Row>
              );
            })()
          )}
        </Table.Body>
      </Table>
    </Box>
  );
};

export const AdminWorkstreamImporter = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();
  const [docUrl, setDocUrl] = useState('');
  const [timeframe, setTimeframe] = useState('');
  const [preview, setPreview] = useState<AdminWorkstreamImporterPreviewResponse | null>(null);
  const [result, setResult] = useState<AdminWorkstreamImporterCreateResponse | null>(null);
  const [debugCopied, setDebugCopied] = useState(false);

  useDocumentTitle('Workstream importer');

  const previewMutation = useMutation({
    mutationFn: () =>
      getService(
        AdminWorkstreamImporterService
      ).previewWorkstreamImportApiV1AdminWorkstreamImporterPreviewPost({
        doc_url: docUrl.trim(),
        timeframe,
      }),
    onMutate: () => {
      setPreview(null);
      setResult(null);
    },
    onSuccess: (data) => {
      setPreview(data);
    },
  });

  const debugMutation = useMutation({
    mutationFn: async () => {
      const debugExport = await getService(
        AdminWorkstreamImporterService
      ).debugWorkstreamImportApiV1AdminWorkstreamImporterDebugPost({
        doc_url: docUrl.trim(),
      });
      await navigator.clipboard.writeText(JSON.stringify(debugExport, null, 2));
      return debugExport;
    },
    onSuccess: () => {
      setDebugCopied(true);
      window.setTimeout(() => setDebugCopied(false), 3000);
    },
  });

  const createMutation = useMutation({
    mutationFn: () => {
      if (!preview) {
        throw new Error('Preview the doc before creating workstreams.');
      }
      return getService(
        AdminWorkstreamImporterService
      ).createWorkstreamImportApiV1AdminWorkstreamImporterCreatePost({
        doc_url: preview.doc_url,
        doc_revision: preview.doc_revision,
        timeframe: preview.timeframe,
      });
    },
    onSuccess: (data) => {
      setResult(data);
    },
  });

  if (!pulseUser) {
    return null;
  }

  if (!isUserAdmin(pulseUser)) {
    navigate('/');
    return null;
  }

  const canCreate = Boolean(preview && preview.valid_row_count > 0 && !result);

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Workstream importer', to: ROUTE_PATHS.ADMIN_WORKSTREAM_IMPORTER},
      ]}
      width="100%"
    >
      <Stack gap="24">
        <Title size={24}>Workstream importer</Title>

        <Box
          borderRadius="Large"
          borderWidth="1"
          borderStyle="Solid"
          borderColor="Border Subtle"
          padding="16"
          backgroundColor="Background Base"
        >
          <Stack gap="12">
            <Text isBold>Paper doc</Text>
            <Box display="flex" style={{gap: 8}}>
              <Box style={{flex: 1}}>
                <TextInput.Container>
                  <TextInput.Input
                    aria-label="Paper doc URL"
                    placeholder="https://www.dropbox.com/scl/fi/..."
                    value={docUrl}
                    onChange={(event) => {
                      setDocUrl(event.currentTarget.value);
                      setDebugCopied(false);
                    }}
                  />
                </TextInput.Container>
              </Box>
              <AdminWorkstreamImporterTimeframeSelect
                value={timeframe}
                onChange={setTimeframe}
                disabled={previewMutation.isPending || createMutation.isPending}
              />
              <Button
                variant="primary"
                onClick={() => previewMutation.mutate()}
                disabled={
                  !docUrl.trim() ||
                  !timeframe ||
                  previewMutation.isPending ||
                  debugMutation.isPending ||
                  createMutation.isPending
                }
                isLoading={previewMutation.isPending}
              >
                Preview
              </Button>
              <Button
                variant="outline"
                onClick={() => debugMutation.mutate()}
                disabled={
                  !docUrl.trim() ||
                  previewMutation.isPending ||
                  debugMutation.isPending ||
                  createMutation.isPending
                }
                isLoading={debugMutation.isPending}
              >
                Debug
              </Button>
            </Box>
          </Stack>
        </Box>

        {previewMutation.error ? (
          <SystemBanner tone="alert">
            <Text>{getErrorMessage(previewMutation.error)}</Text>
          </SystemBanner>
        ) : null}

        {debugMutation.error ? (
          <SystemBanner tone="alert">
            <Text>{getErrorMessage(debugMutation.error)}</Text>
          </SystemBanner>
        ) : null}

        {debugCopied ? (
          <SystemBanner tone="success">
            <Text>Raw Paper export copied to clipboard.</Text>
          </SystemBanner>
        ) : null}

        {createMutation.error ? (
          <SystemBanner tone="alert">
            <Text>{getErrorMessage(createMutation.error)}</Text>
          </SystemBanner>
        ) : null}

        {preview ? (
          <Stack gap="16">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{gap: 12}}
            >
              <Stack gap="4">
                <Title size={18}>Review</Title>
                <Text color="subtle">
                  {preview.doc_title} · {preview.timeframe} · {preview.valid_row_count} valid ·{' '}
                  {preview.invalid_row_count} invalid
                </Text>
              </Stack>
              <Button
                variant="primary"
                onClick={() => createMutation.mutate()}
                disabled={!canCreate || createMutation.isPending}
                isLoading={createMutation.isPending}
              >
                Create
              </Button>
            </Box>
            <ReviewTable preview={preview} result={result} />
          </Stack>
        ) : null}
      </Stack>
    </Layout.Container>
  );
};
